// src/antdviews/components/AccountMgmtContent.tsx
// 账号管理 - 内容区域组件

import axios from 'axios'
import React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import moment from 'moment'
import {
    Layout,
    Table,
    Button,
    Input,
    Space,
    Row,
    Col,
    Card,
    Typography,
    Tag,
    Tooltip,
    Pagination,
    Form,
    Select,
    message,
    Avatar,
} from 'antd'
import {
    SearchOutlined,
    ReloadOutlined,
    UserOutlined,
    MailOutlined,
    IdcardOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    CopyOutlined,
} from '@ant-design/icons'
import { ROOT, UserMgmtApi } from '../../services/Api'
import { Page } from '../../services/Page'
import { ROLE } from '../../services/Macro'

const { Content } = Layout
const { Title, Text } = Typography

interface Account {
    id?: number
    userId?: number
    email?: string
    name?: string
    uuid?: string
    role?: number
    createTime?: string
    isOpen?: boolean
    onlinePortrait?: string
    [key: string]: any
}

interface AccountMgmtContentState {
    accountList: Account[]
    account: {
        list: Account[]
        currentPage: number
        totalPages: number
        totalRecords: number
        pages: number[]
    }
    loginName: string
    page: number
    size: number
    email: string
    name: string
    uuid: string
    loading: boolean
    searchForm: {
        email: string
        name: string
        uuid: string
    }
    columnWidths: {
        email: number
        name: number
    }
}

interface AccountMgmtContentProps extends WithTranslation {
    // 可扩展的父组件回调
    onDataLoaded?: (data: any) => void
}

class AccountMgmtContent extends React.Component<AccountMgmtContentProps, AccountMgmtContentState> {

    constructor(props: AccountMgmtContentProps) {
        super(props)
        this.state = {
            accountList: [],
            account: {
                list: [],
                currentPage: 1,
                totalPages: 1,
                totalRecords: 1,
                pages: [],
            },
            loginName: '',
            page: 1,
            size: 10,
            email: '',
            name: '',
            uuid: '',
            loading: false,
            searchForm: {
                email: '',
                name: '',
                uuid: '',
            },
            columnWidths: {
                email: 200,
                name: 100,
            },
        }
    }

    componentDidMount() {
        this.getAccountList()
    }

    changeSizeAndPage = (size: number, page: number) => {
        this.setState({ size, page }, () => {
            this.getAccountList()
        })
    }

    searchWithFilter = (email: string, name: string, uuid: string) => {
        console.log(`VIEW --- email:`, email)
        this.setState({ email, name, uuid, page: 1 }, () => {
            this.getAccountList()
        })
    }

    handleSearch = (values: { email: string; name: string; uuid: string }) => {
        this.setState({
            searchForm: values,
            email: values.email || '',
            name: values.name || '',
            uuid: values.uuid || '',
            page: 1,
        }, () => {
            this.getAccountList()
        })
    }

    handleReset = () => {
        this.setState({
            searchForm: { email: '', name: '', uuid: '' },
            email: '',
            name: '',
            uuid: '',
            page: 1,
        }, () => {
            this.getAccountList()
        })
    }

    getAccountList = async () => {
        const DEBUG = true
        const TAG = `AccountMgmtContent.getAccountList() - `
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'

        this.setState({ loading: true })

        try {
            const requestData = {
                role: ROLE.SUPER,
                token,
                lang: lang.split('_')[0],
                page: this.state.page,
                size: this.state.size,
                email: this.state.email,
                name: this.state.name,
                uuid: this.state.uuid,
            }
            DEBUG && console.log(TAG, `requestData:\n`, requestData)

            const response = await axios.post(UserMgmtApi.SUPER_GET_ACCOUNT_LIST, requestData)
            DEBUG && console.log(TAG, `获取用户列表:\n`, response.data)

            const accountList = response.data.data.list || []
            const currentPage = response.data.data.currentPage || 1
            const totalPages = response.data.data.totalPages || 1
            const totalRecords = response.data.data.totalRecords || 1

            for (let i = 0; i < accountList.length; i++) {
                const account = accountList[i]
                account.createTime = moment(account.createTime).format('YYYY-MM-DD HH:mm:ss')
                account.isOpen = false
            }

            const pages = []
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1)
            }
            DEBUG && console.log(TAG, `pages:`, pages)

            this.setState({
                account: {
                    list: accountList,
                    currentPage,
                    totalPages,
                    totalRecords,
                    pages,
                },
                accountList,
                loginName: 'Admin',
            })

            // 通知父组件数据已加载
            if (this.props.onDataLoaded) {
                this.props.onDataLoaded({
                    list: accountList,
                    currentPage,
                    totalPages,
                    totalRecords,
                })
            }
        } catch (error) {
            console.error('获取账户列表失败:', error)
            message.error(this.props.t('fetchFailed') || '获取账户列表失败')
        } finally {
            this.setState({ loading: false })
        }
    }

    /** 查看详情 - 跳转到详情页面 */
    showDetail = (record: Account) => {
        const userId = record.id || record.userId || ''
        const uuid = record.uuid || ''
        window.location.href = `${ROOT}/${Page.accountDetail}?userId=${userId}&uuid=${uuid}`
    }

    /** 获取记录的唯一ID */
    getRecordId = (record: Account): string | number => {
        return record.id || record.userId || record.accountId || record.uid || Math.random().toString()
    }

    /** 复制UUID到剪贴板 */
    copyUUID = (uuid: string) => {
        if (!uuid) {
            message.warning('UUID为空，无法复制')
            return
        }
        navigator.clipboard.writeText(uuid).then(() => {
            message.success('UUID已复制到剪贴板')
        }).catch(() => {
            const textarea = document.createElement('textarea')
            textarea.value = uuid
            document.body.appendChild(textarea)
            textarea.select()
            try {
                document.execCommand('copy')
                message.success('UUID已复制到剪贴板')
            } catch (err) {
                message.error('复制失败，请手动复制')
            }
            document.body.removeChild(textarea)
        })
    }

    /** 获取头像 */
    getPortrait = (item: Account) => {
        if (item.onlinePortrait) {
            return item.onlinePortrait
        }
        return '/admin/portrait/default.jpg'
    }

    render() {
        const { t } = this.props
        const { accountList, loading, account, searchForm, columnWidths } = this.state

        // 计算表格滚动高度
        const tableScrollY = window.innerHeight - 330 // 减去头部、搜索栏、分页等占用的高度

        // 判断是否需要滚动：根据数据行数 * 每行高度(约40px) 是否超过可用高度
        const rowHeight = 56 // 表格每行大约高度
        const headerHeight = 38 // 表头高度
        const availableHeight = tableScrollY - headerHeight
        const totalContentHeight = accountList.length * rowHeight
        const needScroll = totalContentHeight > availableHeight

        console.log(`window.innerHeight:`, window.innerHeight)
        console.log(`tableScrollY:`, tableScrollY)
        console.log(`needScroll:`, needScroll)

        // 表格列定义
        const columns = [
            {
                title: t('id') || 'ID',
                dataIndex: 'id',
                key: 'id',
                width: 40,
                render: (text: number, record: Account) => {
                    const id = this.getRecordId(record)
                    return <Text>{id}</Text>
                },
            },
            {
                title: t('portrait') || '头像',
                dataIndex: 'onlinePortrait',
                key: 'portrait',
                width: 60,
                render: (_: string, record: Account) => (
                    <Avatar
                        size={40}
                        shape="square"
                        icon={<UserOutlined />}
                        src={this.getPortrait(record)}
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: 2,
                        }}
                    />
                ),
            },
            {
                title: (
                    <Space size={4}>
                        <span>{t('accountEmail') || '邮箱'}</span>
                    </Space>
                ),
                dataIndex: 'email',
                key: 'email',
                width: columnWidths.email,
                render: (text: string) => (
                    <Space>
                        <MailOutlined style={{ color: '#1890ff' }} />
                        <Text>{text}</Text>
                    </Space>
                ),
            },
            {
                title: (
                    <Space size={4}>
                        <span>{t('nickname') || '昵称'}</span>
                    </Space>
                ),
                dataIndex: 'name',
                key: 'name',
                width: columnWidths.name,
                render: (text: string) => (
                    <Space>
                        <UserOutlined style={{ color: '#52c41a' }} />
                        <Text>{text || '-'}</Text>
                    </Space>
                ),
            },
            {
                title: t('uuid') || 'UUID',
                dataIndex: 'uuid',
                key: 'uuid',
                width: 340,
                render: (text: string) => (
                    <Space size={4}>
                        <Text code style={{ fontSize: 16 }}>
                            {text || '-'}
                        </Text>
                        {text && (
                            <Tooltip title="复制UUID">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined style={{ color: '#1890ff' }} />}
                                    onClick={() => this.copyUUID(text)}
                                    style={{ padding: '0 4px', height: 22 }}
                                />
                            </Tooltip>
                        )}
                    </Space>
                ),
            },
            {
                title: t('createTime') || '注册时间',
                dataIndex: 'createTime',
                key: 'createTime',
                width: 200,
                render: (text: string) => (
                    <Space style={{ whiteSpace: 'nowrap' }}>
                        <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                        <Text type="secondary">{text}</Text>
                    </Space>
                ),
            },
            {
                title: t('operation') || '操作',
                key: 'action',
                width: 100,
                fixed: 'right' as const,
                render: (_: any, record: Account) => (
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => this.showDetail(record)}
                    >
                        {t('detail') || '详情'}
                    </Button>
                ),
            },
        ]

        return (
            <Content
                style={{
                    margin: '4px',
                    padding: '6px',
                    background: 'var(--content-bg)',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Card
                    title={
                        <Space>
                            <UserOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                {t('accountMgmt') || '账号管理'}
                            </Title>
                            <Tag color="blue">{account.totalRecords || 0} {t('records') || '条记录'}</Tag>
                        </Space>
                    }
                    extra={
                        <Button icon={<ReloadOutlined />} onClick={this.getAccountList}>
                            {t('refresh') || '刷新'}
                        </Button>
                    }
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        background: 'var(--card-bg)',
                    }}
                    bodyStyle={{
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    {/* 搜索区域 - 固定不滚动 */}
                    <Card
                        size="small"
                        style={{
                            marginBottom: 16,
                            background: '#fafafa',
                            borderRadius: 6,
                            flexShrink: 0,
                        }}
                        bodyStyle={{ padding: '16px 24px' }}
                    >
                        <Form
                            layout="inline"
                            onFinish={this.handleSearch}
                            style={{ width: '100%' }}
                        >
                            <Row gutter={[16, 16]} style={{ width: '100%' }}>
                                <Col xs={24} sm={8} md={7}>
                                    <Form.Item
                                        name="email"
                                        style={{ width: '100%', marginBottom: 0 }}
                                    >
                                        <Input
                                            prefix={<MailOutlined />}
                                            placeholder={t('accountEmail') || '邮箱'}
                                            allowClear
                                            value={searchForm.email}
                                            onChange={(e) => {
                                                this.setState({
                                                    searchForm: {
                                                        ...this.state.searchForm,
                                                        email: e.target.value,
                                                    },
                                                })
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8} md={7}>
                                    <Form.Item
                                        name="name"
                                        style={{ width: '100%', marginBottom: 0 }}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder={t('nickname') || '昵称'}
                                            allowClear
                                            value={searchForm.name}
                                            onChange={(e) => {
                                                this.setState({
                                                    searchForm: {
                                                        ...this.state.searchForm,
                                                        name: e.target.value,
                                                    },
                                                })
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={8} md={6}>
                                    <Form.Item
                                        name="uuid"
                                        style={{ width: '100%', marginBottom: 0 }}
                                    >
                                        <Input
                                            prefix={<IdcardOutlined />}
                                            placeholder={t('uuid') || 'UUID'}
                                            allowClear
                                            value={searchForm.uuid}
                                            onChange={(e) => {
                                                this.setState({
                                                    searchForm: {
                                                        ...this.state.searchForm,
                                                        uuid: e.target.value,
                                                    },
                                                })
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            htmlType="submit"
                                        >
                                            {t('search') || '搜索'}
                                        </Button>
                                        <Button onClick={this.handleReset}>
                                            {t('reset') || '重置'}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    {/* 表格区域 - 可滚动 */}
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Table
                            columns={columns}
                            dataSource={accountList}
                            rowKey={(record) => String(this.getRecordId(record))}
                            loading={loading}
                            pagination={false}
                            bordered
                            size="small"
                            style={{ borderRadius: 6, height: '100%' }}
                            scroll={{
                                x: 1160,
                                y: needScroll ? (tableScrollY > 200 ? tableScrollY : 300) : undefined,
                            }}
                        />
                    </div>

                    {/* 分页区域 - 固定在底部 */}
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{
                            marginTop: 16,
                            flexShrink: 0,
                            paddingTop: 8,
                            borderTop: '1px solid #f0f0f0',
                        }}
                    >
                        <Col>
                            <Text type="secondary">
                                {t('table.footer.totalRecords') || '共'} {account.totalRecords} {t('table.footer.records') || '条记录'}
                                ，{t('table.footer.currentPage') || '当前第'} {account.currentPage}/{account.totalPages} {t('table.footer.page') || ''}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {t('table.footer.pageSize') || '每页'}
                                </span>
                                <Select
                                    value={this.state.size}
                                    onChange={(value: number) => {
                                        this.changeSizeAndPage(value, 1)
                                    }}
                                    style={{ width: 80 }}
                                >
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={50}>50</Select.Option>
                                    <Select.Option value={100}>100</Select.Option>
                                </Select>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {t('table.footer.items') || '条'}
                                </span>
                                <Pagination
                                    current={account.currentPage}
                                    total={account.totalRecords}
                                    pageSize={this.state.size}
                                    onChange={(page) => {
                                        this.changeSizeAndPage(this.state.size, page)
                                    }}
                                    showSizeChanger={false}
                                    showQuickJumper
                                    locale={{
                                        items_per_page: t('table.footer.itemsPerPage') || '条/页',
                                        jump_to: t('table.footer.jumpTo') || '跳至',
                                        jump_to_confirm: t('table.footer.confirm') || '确定',
                                        page: t('table.footer.page') || '',
                                        prev_page: t('table.footer.prevPage') || '上一页',
                                        next_page: t('table.footer.nextPage') || '下一页',
                                        prev_5: t('table.footer.prev5') || '向前 5 页',
                                        next_5: t('table.footer.next5') || '向后 5 页',
                                        prev_3: t('table.footer.prev3') || '向前 3 页',
                                        next_3: t('table.footer.next3') || '向后 3 页',
                                    }}
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Content>
        )
    }
}

export default withTranslation()(AccountMgmtContent)